import {
   Flex,
   Search,
   Button,
   useI18nContext,
   FlexList,
   SegmentedController,
   DateSelect,
} from "@procore/core-react";
import type { Dispatch, SetStateAction } from "react";
import React, { useRef, useState } from "react";
import { Filter, Sliders, ZoomMinus, ZoomPlus, Fullscreen } from "@procore/core-icons";
import type { ganttFilterType } from "./prop-types";
import { GanttViewMode } from "./prop-types";
import { GanttControlPanelFilterPills } from "./gantt-control-panel-filter-pills";
import type { BryntumGantt } from "@bryntum/gantt-react-thin";
import "./gantt-container.css";
import {
   BryntumModalType,
   SaveViewModal as DefaultSaveViewModal,
   showBryntumModal,
} from "../common/bryntum/bryntum-modals";
import LaunchDarklyClient from "@laborchart-modules/launch-darkly-browser";
import { GanttDateRangeSelector } from "./gantt-date-range-selector";
import type { DateRangeDays } from "./gantt-date-range-constants";

type VoidFn = () => void;
export interface GanttControlPanelProps {
   search: string;
   ganttFilter: ganttFilterType;
   ganttViewMode: GanttViewMode;
   setGanttViewMode: (value: GanttViewMode) => void;
   setSearch: (value: string) => void;
   toggleConfigPanel: VoidFn;
   toggleFilterPanel: VoidFn;
   setGanttFilter: Dispatch<SetStateAction<ganttFilterType>>;
   onViewDateChange: (e: any) => void;
   onZoomOut: VoidFn;
   onZoomIn: VoidFn;
   onZoomToFit: VoidFn;
   fileExportName: string;
   ganttRef?: React.RefObject<BryntumGantt>;
   customFilterFields?: any;
   SaveViewModal?: React.ComponentType<any>;
   selectedDateRange?: DateRangeDays;
   onDateRangeChange?: (days: DateRangeDays, anchor: Date) => void;
}

export const GanttControlPanel = ({
   SaveViewModal = DefaultSaveViewModal,
   search,
   setSearch,
   ganttViewMode,
   setGanttViewMode,
   toggleConfigPanel,
   toggleFilterPanel,
   ganttFilter,
   setGanttFilter,
   fileExportName,
   ganttRef,
   customFilterFields,
   onViewDateChange,
   onZoomOut,
   onZoomIn,
   onZoomToFit,
   selectedDateRange,
   onDateRangeChange,
}: GanttControlPanelProps) => {
   const I18n = useI18nContext();

   const dateSelectRef = useRef(null);
   const [dateSelectValue, setDateSelectValue] = useState<Date>(new Date());
   const onDateSelectChange = (value: Date | null) => {
      setDateSelectValue(value ?? new Date());
      onViewDateChange(value);
   };

   // Instead of passing the isConfigPanelOpen state in as a prop, just initialize it here and let it always default to closed
   const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);

   return (
      <div>
         <Flex direction="row" gap="sm" className="ganttControlPanelWrapper">
            <div style={{ display: "grid", gridTemplateColumns: "auto auto auto 1fr" }}>
               {LaunchDarklyClient.getFlagValue("gantt-show-view-mode-controller") && (
                  <SegmentedController aria-label={"gantt-view-segmented-controller"} block>
                     <SegmentedController.Segment
                        onClick={() => {
                           setGanttViewMode(GanttViewMode.PROJECTS);
                           ganttRef?.current?.instance.taskStore.clearGroupers();
                        }}
                        active={ganttViewMode === GanttViewMode.PROJECTS}
                     >
                        Projects
                     </SegmentedController.Segment>
                     <SegmentedController.Segment
                        onClick={() => {
                           setGanttViewMode(GanttViewMode.RESOURCES);
                           ganttRef?.current?.instance.taskStore.group("resourceId");
                        }}
                        active={ganttViewMode === GanttViewMode.RESOURCES}
                     >
                        Resources
                     </SegmentedController.Segment>
                  </SegmentedController>
               )}
               <div style={{ marginLeft: "14px" }}>
                  <Search
                     aria-label="search"
                     value={search}
                     onSubmit={(value) => setSearch(value)}
                     onClear={() => setSearch("")}
                     placeholder={I18n.t(
                        ganttViewMode === GanttViewMode.PROJECTS
                           ? "views.company.workforce_planning.gantt.search_placeholder"
                           : "views.company.workforce_planning.gantt.search_placeholder_resources",
                     )}
                     style={{ width: "300px" }}
                  />
               </div>
               <Button
                  variant="tertiary"
                  icon={<Filter size="sm" />}
                  onClick={() => {
                     toggleFilterPanel();
                  }}
                  style={{ margin: "0 4px 0 8px", width: "max-content" }}
                  className={"gantt-filter-toggle-btn"}
                  data-testid="filter-panel-toggle-btn"
               />
               <GanttControlPanelFilterPills
                  ganttFilter={ganttFilter}
                  setGanttFilter={setGanttFilter}
                  customFilterFields={customFilterFields}
                  ganttViewMode={ganttViewMode}
               />
            </div>
            <FlexList space="xs">
               <DateSelect
                  ref={dateSelectRef}
                  onChange={onDateSelectChange}
                  value={dateSelectValue}
                  onClear={() => ((dateSelectRef.current as any).instance.value = new Date())}
                  data-testid="date-select-input"
               />
               {selectedDateRange !== undefined && onDateRangeChange && (
                  <GanttDateRangeSelector
                     selectedRange={selectedDateRange}
                     onRangeChange={(days) => onDateRangeChange(days, dateSelectValue)}
                  />
               )}
               <Button
                  icon={<ZoomMinus size="sm" />}
                  onClick={onZoomOut}
                  variant="tertiary"
                  data-testid="zoom-out-btn"
               />
               <Button
                  icon={<ZoomPlus size="sm" />}
                  onClick={onZoomIn}
                  variant="tertiary"
                  data-testid="zoom-in-btn"
               />
               <Button
                  icon={<Fullscreen size="sm" />}
                  onClick={onZoomToFit}
                  variant="tertiary"
                  data-testid="zoom-to-fit-btn"
               />
               <Button
                  variant="tertiary"
                  onClick={() => {
                     showBryntumModal({
                        type: BryntumModalType.ExportView,
                        modalId: "gantt-export-modal-container",
                        title: "Export View",
                        onSave: (modalState: any) => {
                           if (!ganttRef?.current) return;
                           const columns =
                              ganttViewMode === GanttViewMode.PROJECTS
                                 ? [
                                      "name",
                                      "jobTitleName",
                                      "startDate",
                                      "endDate",
                                      "taskStartDate",
                                      "taskEndDate",
                                   ]
                                 : ["resourceId", "taskStartDate", "taskEndDate"];
                           if (modalState.exportType === "pdf") {
                              // v7: columns may not be in PrintConfig types but supported at runtime
                              ganttRef?.current?.instance.features.print.print({
                                 scheduleRange: "currentview",
                                 rowsRange: "all",
                                 exporterType: "multipagevertical",
                                 paperFormat: modalState.exportPageSize,
                                 columns: columns,
                                 fileName: fileExportName,
                              } as any);
                           } else if (modalState.exportType === "csv") {
                              ganttRef.current?.instance.features.excelExporter.export({
                                 filename: fileExportName,
                                 csv: true,
                              });
                           }
                        },
                     });
                  }}
                  style={{ backgroundColor: "#E3E6E8" }}
               >
                  Export
               </Button>
               {/* This modal works a bit differently than the others because it's shared between pages and is a bit more heavily abstracted */}
               <SaveViewModal
                  search={search}
                  viewConfig={{
                     search,
                     ganttViewMode,
                     ganttFilter,
                     customFilterFields,
                     selectedDateRange,
                  }}
               />
               <Button
                  variant="tertiary"
                  icon={<Sliders size="sm" />}
                  onClick={() => {
                     toggleConfigPanel();
                     setIsConfigPanelOpen(!isConfigPanelOpen);
                  }}
                  data-testid="config-panel-toggle-btn"
                  className={isConfigPanelOpen ? "controlPanelButtonActive" : ""}
               />
            </FlexList>
         </Flex>
      </div>
   );
};
